var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import './TimelineView.scss';
import dayjs from 'dayjs';
import { FULL_ROUTES } from '@sonar-software/sonar-ts-constants';
import { getQueryString } from '@sonar-software/sonar-http';
import { keyPaginationFallbackQueryParams } from '@ui/KeyPagination';
import { TimelineDetailsTable } from './DetailsTable';
import { TimelineSummary } from './Summary';
import { coreActions, TimelineGroupingType } from '@sonar-software/sonar-core';
import { getPastDate, parseQueryString } from '@sonar-software/sonar-ts-lib';
import { parseObjectFilter, stringifyObjectFilter } from './@arch/lib';
import React, { useCallback } from 'react';
import { SonarCard, ViewArea } from '@ui/general';
import { useAction, useCurrentCustomer, useOnMount, useOnUnmount, useQueryParams } from '@hooks';
import { useLocation, useNavigate } from 'react-router-dom';
function isDetailsParamChange(params) {
    const { count, key, sortBy, sortDirection, paginationDirection } = params, rest = __rest(params, ["count", "key", "sortBy", "sortDirection", "paginationDirection"]);
    const hasOtherKeys = Object.keys(rest).length > 0;
    if (hasOtherKeys) {
        return false;
    }
    return Boolean(count) ||
        Boolean(paginationDirection) ||
        Boolean(key) ||
        (!key && Object.keys(params).includes('key')) ||
        Boolean(sortBy) ||
        Boolean(sortDirection);
}
export const defaultTimelineFilterParams = {
    applicationIds: [],
    isFollowing: undefined,
    modifiedByUsernames: [],
    objectFilter: {},
    objectNameFilter: '',
    system: true,
    tagIds: []
};
export const defaultTimelineParams = Object.assign(Object.assign({}, defaultTimelineFilterParams), { end: '', start: '', timelineGroupingType: TimelineGroupingType.Day, tzoffset: 0 });
export const TimelineView = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { timelineRetentionPeriod } = useCurrentCustomer();
    const getTimelineDetails = useAction(coreActions.requestGetCoreTimelineDetails);
    const getTimelineGroupedSummary = useAction(coreActions.requestGetCoreTimelineGroupedSummary);
    const getTimelineSummary = useAction(coreActions.requestGetCoreTimelineSummary);
    const getTimelineSynopsis = useAction(coreActions.requestGetCoreTimelineSynopsis);
    const getTimelineUserChanges = useAction(coreActions.requestGetCoreTimelineUserChanges);
    const resetTimelineDetails = useAction(coreActions.resetGetCoreTimelineDetails);
    const resetTimelineGroupedSummary = useAction(coreActions.resetGetCoreTimelineGroupedSummary);
    const resetTimelineSummary = useAction(coreActions.resetGetCoreTimelineSummary);
    const resetTimelineSynopsis = useAction(coreActions.resetGetCoreTimelineSynopsis);
    const resetTimelineUserChanges = useAction(coreActions.resetGetCoreTimelineUserChanges);
    const updateUrl = useCallback((queryObject) => {
        const url = `${FULL_ROUTES.TIMELINE}?${getQueryString(queryObject)}`;
        navigate(url, { replace: true });
    }, [navigate]);
    const { queryParams, updateParams } = useQueryParams({
        defaultParams: Object.assign(Object.assign({}, defaultTimelineParams), { start: getPastDate(Math.min(timelineRetentionPeriod !== null && timelineRetentionPeriod !== void 0 ? timelineRetentionPeriod : 30, 30)) }),
        fallbackParams: keyPaginationFallbackQueryParams,
        onUpdate: (newParams, prev, combined) => {
            const singleChange = Object.keys(newParams).length === 1;
            const onlyGroupingChange = singleChange && newParams.timelineGroupingType;
            const onlyModifiedByChange = singleChange && newParams.modifiedByUsernames;
            const { count, key, objectFilter, paginationDirection, sortBy, sortDirection, timelineGroupingType } = combined, summaryRequest = __rest(combined, ["count", "key", "objectFilter", "paginationDirection", "sortBy", "sortDirection", "timelineGroupingType"]);
            const filterParams = Object.assign(Object.assign({}, summaryRequest), { objectFilter: stringifyObjectFilter(objectFilter !== null && objectFilter !== void 0 ? objectFilter : {}) });
            const detailParams = Object.assign(Object.assign({}, filterParams), { count,
                key,
                paginationDirection,
                sortBy,
                sortDirection });
            updateUrl(Object.assign(Object.assign({}, combined), { objectFilter: filterParams.objectFilter }));
            if (isDetailsParamChange(newParams)) {
                getTimelineDetails(detailParams);
                return;
            }
            getTimelineGroupedSummary(Object.assign(Object.assign({}, filterParams), { timelineGroupingType }));
            if (!onlyGroupingChange && !onlyModifiedByChange) {
                const { modifiedByUsernames, system } = filterParams, userChangesParams = __rest(filterParams, ["modifiedByUsernames", "system"]);
                getTimelineUserChanges(userChangesParams);
            }
            if (!onlyGroupingChange) {
                getTimelineSummary(summaryRequest);
                getTimelineSynopsis(filterParams);
                getTimelineDetails(detailParams);
            }
        }
    });
    useOnMount(() => {
        var _a;
        const urlParams = parseQueryString(location.search);
        updateParams(Object.assign(Object.assign({}, urlParams), { objectFilter: parseObjectFilter((_a = urlParams.objectFilter) !== null && _a !== void 0 ? _a : ''), tzoffset: dayjs().utcOffset() }));
    });
    useOnUnmount(() => {
        resetTimelineDetails();
        resetTimelineGroupedSummary();
        resetTimelineSummary();
        resetTimelineSynopsis();
        resetTimelineUserChanges();
    });
    return (React.createElement(ViewArea, { direction: 'vertical', fullWidth: true, id: 'timeline-view', showPeriscopes: true },
        React.createElement(SonarCard, { noSpacing: true },
            React.createElement(TimelineSummary, { onChange: updateParams, params: queryParams }),
            React.createElement(TimelineDetailsTable, { onChange: updateParams, params: queryParams }))));
};
